import { formatDate } from "@angular/common";
import { ErrorHelper, StringHelper } from "@tylertech/shared-module";
import { LogLevel } from "../logLevel";

const RankedLogLevel: Readonly<Record<LogLevel, number>> = {
    ERROR: 0,
    WARNING: 1,
    INFORMATION: 2,
    DEBUG: 3,
    TRACE: 4,
};

// TODO: split logging and configuration methods
export interface ILogger
{
    enabled: boolean;

    getLogLevel(): LogLevel;
    setLogLevel(value: LogLevel): void;

    error(message: string | Error | unknown, ...args: Array<unknown>): void;
    warning(message: string | Error, ...args: Array<unknown>): void;
    information(message: string, ...args: Array<unknown>): void;
    debug(message: string, ...args: Array<unknown>): void;
    trace(message: string, ...args: Array<unknown>): void;
}

type LogFn = (message: string | Error | unknown, ...args: Array<unknown>) => void;

class LoggerImpl implements ILogger
{
    minimumLevel: LogLevel;

    constructor()
    {
        this.minimumLevel = LogLevel.INFORMATION;
    }

    get enabled(): boolean { return true; }

    set logLevel(value: LogLevel)
    {
        this.minimumLevel = value;
    }

    getLogLevel(): LogLevel
    {
        return this.minimumLevel;
    }

    setLogLevel(level: LogLevel): void
    {
        this.logLevel = level;
    }

    error(message: string | Error | unknown, ...args: Array<unknown>): void
    {
        if (message instanceof Error && args.length === 0)
        {
            console.error(message);
            return;
        }

        this.logMessage(LogLevel.ERROR, message, ...args);
    }

    warning(message: string | Error, ...args: Array<unknown>): void
    {
        this.logMessage(LogLevel.WARNING, message, ...args);
    }

    information(message: string, ...args: Array<unknown>): void
    {
        this.logMessage(LogLevel.INFORMATION, message, ...args);
    }

    debug(message: string, ...args: Array<unknown>): void
    {
        this.logMessage(LogLevel.DEBUG, message, ...args);
    }

    trace(message: string, ...args: Array<unknown>): void
    {
        this.logMessage(LogLevel.TRACE, message, ...args);
    }

    private logMessage(logLevel: LogLevel, message: string | Error | unknown, ...args: Array<unknown>): void
    {
        if (RankedLogLevel[logLevel] > RankedLogLevel[this.minimumLevel])
        {
            return;
        }

        const logFn = this.getLogFn(logLevel);
        const levelString = ` ${logLevel}            `.substring(0, LogLevel.INFORMATION.length + 2);
        const msgStr = !(typeof message === "string")
            ? StringHelper.stringify(message, undefined, 4)
            : message;
        const record = `${getLocalDateString()}|${levelString}| ${msgStr}`;

        if (args?.length)
        {
            logFn(record, ...args.map(replaceError));
        }
        else
        {
            logFn(record);
        }

        function replaceError(x: unknown): unknown
        {
            return x instanceof Error
                ? ErrorHelper.toJson(x)
                : x;
        }

    }

    private getLogFn(logLevel: LogLevel): LogFn
    {
        switch (logLevel)
        {
            case LogLevel.ERROR:
                return console.error;
            case LogLevel.WARNING:
                return console.warn;
            case LogLevel.DEBUG:
                return console.debug;
            case LogLevel.TRACE:
                return console.trace;
            default:
                return console.info;
        }
    }
}

function getLocalDateString(): string
{
    // const localOffset = new Date().getTimezoneOffset();
    // const offsetMilliseconds = localOffset * 60000;
    const now = new Date(Date.now())/* new Date(Date.now() - offsetMilliseconds) */;
    return formatDate(now, "HH:mm:ss:SSS", "en-US");
}


// eslint-disable-next-line @typescript-eslint/naming-convention
export const Logger: ILogger = new LoggerImpl();

declare global
{
    interface Window
    {
        tt_logger: ILogger;
    }
}

window.tt_logger = Logger;
